import { useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import {
  ButtonUI,
  Checkbox,
  CustomThemeProvider,
  Input,
  Prompt,
  showNotification,
  usePortal
} from 'sonata-react-components';
import { duplicateInsertionOrder } from 'sonata-static-storage/lib/entities';

import type { LineItem } from 'sonata-static-storage/lib/entities/lineItem/lineItem.types';

import './duplicateIOModal.styles.scss';

type Props = {
  isModalOpened: boolean;
  onClose: () => void;
  ioId: string;
  ioName: string;
  userId: string;
  onSuccess: () => void;
  lineItemIds?: LineItem['_id'][];
};

type State = {
  duplicateLineItems: boolean;
  duplicateName: string;
};

export function DuplicateIOModal({ isModalOpened, onClose, ioId, ioName, userId, onSuccess, lineItemIds }: Props) {
  const [{ duplicateName, duplicateLineItems }, setState] = useState<State>({
    duplicateLineItems: false,
    duplicateName: `${ioName} - Duplicate`
  });
  const { formatMessage } = useIntl();
  const targetBody = usePortal('ioDuplicationModal');

  function handleCheckboxChange() {
    setState(prev => ({ ...prev, duplicateLineItems: !prev.duplicateLineItems }));
  }

  const duplicateIOMutation = useMutation({
    mutationFn: async () => {
      await duplicateInsertionOrder(ioId, userId, duplicateName, duplicateLineItems ? lineItemIds : undefined);
    },
    mutationKey: ['duplicate-io-modal', ioId, userId],
    onError: () => {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Failure duplicating the Insertion Order',
          id: 'duplicateIOModal.notifications.notDuplicated'
        })
      );
    },
    onSuccess
  });

  if (!isModalOpened) {
    return null;
  }

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast="light">
      <Prompt className="duplicateIOPrompt" closePrompt={onClose} width="770px">
        <Prompt.Icon icon="duplicateIOPrompt__icon far fa-copy" />
        <Prompt.Title className="duplicateIOPrompt__title" variant="default">
          {formatMessage({
            defaultMessage: 'Duplicate Insertion Order',
            id: 'duplicateIOModal.title'
          })}
        </Prompt.Title>
        <Prompt.Description className="duplicateIOPrompt__description" variant="default">
          {formatMessage({
            defaultMessage: 'All details from the original Insertion Order will be copied.',
            id: 'duplicateIOModal.description'
          })}
        </Prompt.Description>
        <main className="duplicateIOPrompt__main">
          <Input
            className="duplicateIOPrompt__nameInput"
            id="duplicateIOPrompt__nameInput"
            label={formatMessage({ defaultMessage: 'New Insertion Order Name', id: 'duplicateIOModal.nameLabel' })}
            name="sharedReportNameInput"
            onChange={value => setState(prev => ({ ...prev, duplicateName: typeof value === 'string' ? value : '' }))}
            shape="squareUI"
            type="text"
            value={duplicateName}
          />
          <Checkbox
            active={duplicateLineItems}
            id={'insertionOrder-duplicate-lineItems'}
            labelText={formatMessage({
              defaultMessage: 'Copy line items associated with this insertion order',
              id: 'duplicateIOModal.options.duplicateLineItems'
            })}
            onClickAction={handleCheckboxChange}
          />
        </main>
        <Prompt.Buttons className="duplicateIOPrompt__buttons">
          <ButtonUI
            id={`duplicateIOPrompt__cancel`}
            onClick={onClose}
            size="medium"
            text={formatMessage({
              defaultMessage: 'Cancel',
              id: 'duplicateIOModal.button.cancel'
            })}
            type="secondary"
          />
          <ButtonUI
            id={`duplicateIOPrompt__duplicate`}
            loading={duplicateIOMutation.isLoading}
            onClick={() => duplicateIOMutation.mutate()}
            size="medium"
            text={formatMessage({
              defaultMessage: 'Confirm & Duplicate',
              id: 'duplicateIOModal.button.duplicate'
            })}
            type="primary"
          />
        </Prompt.Buttons>
      </Prompt>
    </CustomThemeProvider>,
    targetBody
  );
}
